import { Box, Flex, Icon, Stack, Text } from '@chakra-ui/core';
import { motion } from 'framer-motion';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { RiQuestionnaireFill } from 'react-icons/ri';
import colors from '../../../../../styles/colors';
import Label from '../../../../common/Label/Label';
import { EmptyQuestionsView } from '../questions/EmptyQuestionsView';
import { QuestionType } from '../questions/QuestionType';
import { questionsViewWrapper } from '../specificQuestions/Questions.styles';
import DocumentUploadQuestionsPresenter from './documentUploadQuestionsPresenter';
import { AnswerFile, normaliseAnswers } from './documentRenderer.types';
import AnswerRenderer from './components/AnswerRenderer';

const MotionBox = motion.custom(Box);
const MotionStack = motion.custom(Stack);

const parentVariants = {
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
  hidden: { transition: { staggerChildren: 0.01, staggerDirection: -1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

interface BaseQuestionViewProps {
  activeQuestionType: QuestionType;
  hasNoQuestionOnPosition: boolean;
}

interface DocumentUploadQuestionsViewProps extends BaseQuestionViewProps {
  questions: DocumentUploadQuestionsPresenter | undefined;
  initialized: boolean;
}

const DocumentUploadQuestionsView: React.FC<DocumentUploadQuestionsViewProps> = React.memo(
  ({ questions, hasNoQuestionOnPosition, activeQuestionType, initialized }) => {
    const { t } = useTranslation();

    if (!initialized) return null;

    const total = questions?.getQuestionsCount() ?? 0;
    const answered = questions?.getAnsweredQuestionsCount() ?? 0;

    if (hasNoQuestionOnPosition || total === 0) {
      if (activeQuestionType === QuestionType.ALL_QUESTIONS) return null;
      return <EmptyQuestionsView message={t('questions.noDocumentsSubmitted')} />;
    }

    return (
      <Fragment>
        <MotionStack
          key="DocumentUploadQuestionsMotionStack"
          initial="hidden"
          animate="visible"
          variants={parentVariants}
          spacing={0}
          padding={2}
          css={questionsViewWrapper}
          data-testid="DocumentUploadQuestions"
        >
          {total > 0 && (
            <Flex justify="flex-start" align="baseline" mb={2}>
              <Label color={colors.gray['500']} colorDarkMode={colors.gray['600']} fontWeight="600">
                {t('questions.totalQuestionAnswered', { count: answered, total })}
              </Label>
            </Flex>
          )}

          {questions?.questions.map((documentQuestion, index) => {
            const { id, answers, skipped, question } = documentQuestion;

            // Normalise answers: guard against non-array values from Firestore
            const answerFiles: AnswerFile[] = normaliseAnswers(Array.isArray(answers) ? answers : /* istanbul ignore next */ []);
            const hasDocuments = answerFiles.length > 0;

            return (
              <MotionBox key={id} variants={cardVariants} mb={4}>
                <Box
                  border="1.5px dashed"
                  borderColor={skipped ? colors.californiaGold : 'gray.200'}
                  borderRadius="12px"
                  bg="white"
                  overflow="hidden"
                >
                  <Box
                    p={4}
                    borderRadius={hasDocuments && !skipped ? '12px 12px 0 0' : '12px'}
                    bg={skipped ? '#F7F4E8' : 'white'}
                  >
                    <Text fontSize="sm" fontWeight="400" color="gray.700" lineHeight="1.6">
                      {`${index + 1}. ${question}`}
                    </Text>

                    {!hasDocuments && !skipped && (
                      <Text fontSize="sm" textAlign="left" borderRadius="14px" style={{ color: '#000', opacity: 0.2 }}>
                        {t('documentUploadQuestions.unanswered')}
                      </Text>
                    )}
                  </Box>

                  {hasDocuments && !skipped && (
                    <Box px={4} pb={4}>
                      <AnswerRenderer answers={answerFiles} maxItemsPerRow={2} />
                    </Box>
                  )}

                  {skipped && (
                    <Box display="flex" alignItems="center" justifyContent="center" py={4} px={4}>
                      <Icon as={RiQuestionnaireFill} color={colors.californiaGold} size={24} />
                      <Text fontSize="sm" fontWeight="bold" color="#2D3748" ml={2}>
                        {t('documentUploadQuestions.skipped')}
                      </Text>
                    </Box>
                  )}
                </Box>
              </MotionBox>
            );
          })}
        </MotionStack>
      </Fragment>
    );
  },
);

DocumentUploadQuestionsView.displayName = 'DocumentUploadQuestionsView';

export default DocumentUploadQuestionsView;
